import { useState, useEffect } from "react"
import { db, ref, set, onValue, auth, get } from "../Firebase/firestore"
import { onAuthStateChanged } from "firebase/auth"
import {
  Truck,
  MapPin,
  Package,
  Calendar,
  User,
  Phone,
  FileText,
  Clock,
  ChevronLeft,
  ChevronRight,
  TimerIcon,
  Building,
  Bell,
} from "lucide-react"
import "./transport-goods-reply.css"
import { update } from "../Firebase/firestore"
import { FaRupeeSign } from "react-icons/fa"
import { push } from "firebase/database"
import InfoIcon from "./infoIcon"

const TransportGoodsReplies = ({ adminView = false, selectedBrand = "" }) => {
  const [supplierId, setSupplierId] = useState(null)
  const [requests, setRequests] = useState([])
  const [quotations, setQuotations] = useState({})
  const [submittedForms, setSubmittedForms] = useState({})

  // Get the brand from props if in admin view, otherwise from localStorage
  const [brand, setBrand] = useState(adminView ? selectedBrand : localStorage.getItem("supplierBrand"))

  // Update brand when selectedBrand changes in admin view
  useEffect(() => {
    if (adminView && selectedBrand) {
      setBrand(selectedBrand)
    }
  }, [adminView, selectedBrand])

  const [loading, setLoading] = useState(true)
  const [previousSubmissions, setPreviousSubmissions] = useState([])
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const gifUrl = "/Images/New notification.gif"

  const sendNotification = async (userId, title, message) => {
    console.log("Sending notification with brand:", brand)
    const notificationRef = ref(db, `user_notifications/transportGoods/${userId}`)
    const newNotificationRef = push(notificationRef)
    const newNotification = {
      title,
      message,
      gifUrl,
      timestamp: new Date().toISOString(),
      seen: false,
      brand: brand, // Add brand to notification
    }
    try {
      await set(newNotificationRef, newNotification)
      console.log("Notification sent successfully")

      // Only send to admin notifications, not to supplier
      // Remove the supplier notification code and only keep admin notification
      const adminNotificationRef = ref(db, `admin_notifications/transportGoods/${userId}`)
      const newAdminNotificationRef = push(adminNotificationRef)
      await set(newAdminNotificationRef, {
        ...newNotification,
        userId: userId, // Include the user ID in admin notification
      })

      // Show notification in UI
      setNotificationMessage(message)
      setShowNotification(true)
      setTimeout(() => setShowNotification(false), 5000)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setSupplierId(user.uid)
      } else {
        setSupplierId(null)
      }
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    // Fetch transport requests - modified to show all requests, not just unreplied ones
    const transportRef = ref(db, `transport_goods`)
    const unsubscribe = onValue(transportRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val()
        const requestList = []

        Object.entries(data).forEach(([userId, transports]) => {
          Object.entries(transports).forEach(([transportId, details]) => {
            // Check if this brand has already responded
            const alreadyResponded = details[`${brand}Response`] !== undefined

            // Include all requests, but mark if this brand has already responded
            requestList.push({
              userId,
              transportId,
              ...details,
              alreadyResponded,
            })
          })
        })

        // Sort requests - unreplied first, then by timestamp
        requestList.sort((a, b) => {
          if (a.alreadyResponded !== b.alreadyResponded) {
            return a.alreadyResponded ? 1 : -1
          }
          return new Date(b.timestamp) - new Date(a.timestamp)
        })

        setRequests(requestList)
      } else {
        setRequests([])
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [brand])

  // Fetch notifications
  useEffect(() => {
    if (!supplierId) return

    const notificationsRef = ref(db, `supplier_notifications/transportGoods/${supplierId}`)
    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const notificationsData = snapshot.val()
        const notificationsList = Object.entries(notificationsData).map(([id, data]) => ({
          id,
          ...data,
        }))

        setNotifications(notificationsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)))
      } else {
        setNotifications([])
      }
    })

    return () => unsubscribe()
  }, [supplierId])

  useEffect(() => {
    // Fetch previously submitted quotations
    const fetchPreviousSubmissions = async () => {
      try {
        const repliesRef = ref(db, `transport_goods_replies`)
        const snapshot = await get(repliesRef)

        if (snapshot.exists()) {
          const data = snapshot.val()
          const submissionsList = []

          for (const [userId, transports] of Object.entries(data)) {
            for (const [transportId, brands] of Object.entries(transports)) {
              if (brands[brand]) {
                const transportRef = ref(db, `transport_goods/${userId}/${transportId}`)
                const transportSnapshot = await get(transportRef)

                if (transportSnapshot.exists()) {
                  const transportDetails = transportSnapshot.val()
                  submissionsList.push({
                    userId,
                    transportId,
                    ...brands[brand],
                    destinationFrom: transportDetails.destinationFrom || "N/A",
                    destinationTo: transportDetails.destinationTo || "N/A",
                    commodity: transportDetails.commodity || "N/A",
                    quantity: transportDetails.quantity || "N/A",
                    submittedBy: brand,
                  })
                }
              }
            }
          }

          // Sort submissions by timestamp (newest first)
          submissionsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          setPreviousSubmissions([...submissionsList])
        }
      } catch (error) {
        console.error("Error fetching previous submissions:", error)
      }
    }

    fetchPreviousSubmissions()
  }, [brand])

  const handleQuotationChange = (transportId, field, value) => {
    setQuotations((prev) => ({
      ...prev,
      [transportId]: { ...prev[transportId], [field]: value },
    }))
  }

  const validateForm = (transportId) => {
    const fields = ["price", "availability", "estimatedDelivery", "transporterName", "transporterContact"]
    const quotation = quotations[transportId] || {}

    return fields.every((field) => quotation[field] && quotation[field].trim() !== "")
  }

  const submitQuotation = async (userId, transportId, request) => {
    console.log("Submitting quotation with brand:", brand)
    if (!supplierId) {
      alert("You must be logged in to submit a quotation.")
      return
    }

    if (!brand) {
      alert("Please select a brand before submitting a quotation.")
      return
    }

    if (!validateForm(transportId)) {
      alert("Please fill in all required fields.")
      return
    }

    const quotationData = {
      price: quotations[transportId]?.price || "",
      availability: quotations[transportId]?.availability || "",
      estimatedDelivery: quotations[transportId]?.estimatedDelivery || "",
      transporter: {
        name: quotations[transportId]?.transporterName || "",
        contact: quotations[transportId]?.transporterContact || "",
      },
      notes: quotations[transportId]?.notes || "",
      timestamp: new Date().toISOString(),
      brand: brand, // Add brand to the quotation data
    }

    try {
      // Store the response under the brand name in transport_goods_replies
      const quotationRef = ref(db, `transport_goods_replies/${userId}/${transportId}/${brand}`)
      await set(quotationRef, quotationData)

      // Add brand response flag to the original request
      const transportRef = ref(db, `transport_goods/${userId}/${transportId}`)
      await update(transportRef, { [`${brand}Response`]: true })

      // Update UI
      setRequests((prev) =>
        prev.map((req) =>
          req.transportId === transportId ? { ...req, alreadyResponded: true, [`${brand}Response`]: true } : req,
        ),
      )

      setSubmittedForms((prev) => ({ ...prev, [transportId]: true }))

      // Add to previous submissions
      const requestDetails = requests.find((req) => req.transportId === transportId)
      if (requestDetails) {
        const newSubmission = {
          userId,
          transportId,
          ...quotationData,
          destinationFrom: requestDetails.destinationFrom,
          destinationTo: requestDetails.destinationTo,
          commodity: requestDetails.commodity,
          quantity: requestDetails.quantity,
          submittedBy: brand,
        }

        setPreviousSubmissions((prev) => [newSubmission, ...prev])
      }

      // Send notification with brand information
      await sendNotification(
        userId,
        "Transport Response",
        `Your transport request from ${requestDetails?.destinationFrom} to ${requestDetails?.destinationTo} has received a response from ${brand}.`,
      )

      setNotificationMessage("Quotation submitted successfully!")
      setShowNotification(true)
      setTimeout(() => setShowNotification(false), 5000)
    } catch (error) {
      console.error("Error submitting quotation:", error)
      alert("There was an error submitting your request. Please try again later.")
    }
  }

  const goToPreviousRequest = () => {
    if (currentRequestIndex > 0) {
      setCurrentRequestIndex(currentRequestIndex - 1)
    }
  }

  const goToNextRequest = () => {
    if (currentRequestIndex < requests.length - 1) {
      setCurrentRequestIndex(currentRequestIndex + 1)
    }
  }

  const toggleTooltip = () => {
    setTooltipVisible(!tooltipVisible)
  }

  if (loading) {
    return (
      <div className="transport-loading">
        <div className="spinner"></div>
        <p>Loading transport requests...</p>
      </div>
    )
  }

  return (
    <div className="container-fluid transport-fluid">
      {showNotification && (
        <div className="notification-toast">
          <Bell className="notification-icon" />
          <span>{notificationMessage}</span>
          <button className="close-notification" onClick={() => setShowNotification(false)}>
            ×
          </button>
        </div>
      )}

      <div className="transport-page-container">
        <div className="transport-replies-container">
          <div className="transport-header">
            <Truck className="transport-icon" />
            <h3 className="title-heading">
              Transport Requests
              <InfoIcon
                text=" This section allows you to respond to transport requests from customers. You can provide pricing,
                    availability, and other details. Multiple suppliers can respond to the same request."
              />
            </h3>

            {/* Notification badge */}
            {notifications.length > 0 && (
              <div className="notification-badge">
                <Bell size={18} />
                <span>{notifications.length}</span>
              </div>
            )}
          </div>

          {requests.length === 0 ? (
            <div className="no-requests">
              <img src="/placeholder.svg?height=120&width=120" alt="No requests" className="no-data-img" />
              <p>No transport requests available at the moment.</p>
            </div>
          ) : (
            <div className="transport-request-slider">
              <div className="slider-controls">
                <button
                  className="slider-button prev"
                  onClick={goToPreviousRequest}
                  disabled={currentRequestIndex === 0}
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="slider-indicator">
                  {currentRequestIndex + 1} / {requests.length}
                </div>
                <button
                  className="slider-button next"
                  onClick={goToNextRequest}
                  disabled={currentRequestIndex === requests.length - 1}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

      {requests.map((request, index) => (
  <div
    key={request.transportId}
    className={`tr-card ${index === currentRequestIndex ? "tr-active" : "tr-inactive"}`}
  >
    <div className="tr-header">
      <div className="tr-route-info">
        <MapPin size={16} className="tr-icon-from" />
        <span className="tr-location">{request.destinationFrom}</span>
        <span className="tr-arrow">➡️</span>
        <MapPin size={16} className="tr-icon-to" />
        <span className="tr-location">{request.destinationTo}</span>
      </div>
    </div>
    <span className={`tr-status-badge ${request[`${brand}Response`] ? "tr-responded" : "tr-pending"}`}>
      {request[`${brand}Response`] ? "Responded" : "Pending"}
    </span>

    <div className="tr-details">
      <div className="tr-detail-item">
        <Building size={16} />
        <span>
          <strong>Company Name:</strong> {request.companyName}
        </span>
      </div>
      <div className="tr-detail-item">
        <Package size={16} />
        <span>
          <strong>Commodity:</strong> {request.commodity}
        </span>
      </div>
      <div className="tr-detail-item">
        <Package size={16} />
        <span>
          <strong>Quantity:</strong> {request.quantity} bags
        </span>
      </div>
      <div className="tr-detail-item">
        <TimerIcon size={16} />
        <span>
          <strong>Submitted:</strong> {new Date(request.timestamp).toLocaleString()}
        </span>
      </div>
    </div>


                  {/* Show response if already responded */}
                  {request[`${brand}Response`] && (
                    <div className="existing-response">
                      <h4>Your Response</h4>
                      <p>You have already responded to this request.</p>
                      {previousSubmissions.find((sub) => sub.transportId === request.transportId) && (
                        <div className="response-details">
                          <p>
                            <strong>Price:</strong>{" "}
                            {previousSubmissions.find((sub) => sub.transportId === request.transportId).price}
                          </p>
                          <p>
                            <strong>Availability:</strong>{" "}
                            {previousSubmissions.find((sub) => sub.transportId === request.transportId).availability}
                          </p>
                          <p>
                            <strong>Estimated Delivery:</strong>{" "}
                            {
                              previousSubmissions.find((sub) => sub.transportId === request.transportId)
                                .estimatedDelivery
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show form if not responded yet */}
                  {!request[`${brand}Response`] && !submittedForms[request.transportId] && (
                    <div className="quotation-form">
                      <h4>Your Quotation</h4>

                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <FaRupeeSign size={16} /> Price <span className="required">*</span>
                          </label>
                          <input
                            type="number"
                            placeholder="Enter price"
                            onChange={(e) => handleQuotationChange(request.transportId, "price", e.target.value)}
                            className="transport-input"
                            min="0"
                            max="1000000"
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <Calendar size={16} /> Availability <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="When available"
                            onChange={(e) => handleQuotationChange(request.transportId, "availability", e.target.value)}
                            className="transport-input"
                            maxLength="100"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <Calendar size={16} /> Estimated Delivery <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Delivery timeline"
                          onChange={(e) =>
                            handleQuotationChange(request.transportId, "estimatedDelivery", e.target.value)
                          }
                          className="transport-input"
                          maxLength="100"
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <User size={16} /> Transporter Name <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Transporter name"
                            onChange={(e) =>
                              handleQuotationChange(request.transportId, "transporterName", e.target.value)
                            }
                            className="transport-input"
                            maxLength="50"
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <Phone size={16} /> Transporter Contact <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Contact number"
                            onChange={(e) =>
                              handleQuotationChange(request.transportId, "transporterContact", e.target.value)
                            }
                            className="transport-input"
                            maxLength="10"
                            pattern="[0-9]+"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <FileText size={16} /> Notes (Optional)
                        </label>
                        <textarea
                          placeholder="maximum 500 characters"
                          onChange={(e) => handleQuotationChange(request.transportId, "notes", e.target.value)}
                          className="transport-input"
                          maxLength="500"
                        ></textarea>
                      </div>

                      <div className="required-fields-note">
                        <span>
                          Fields marked with <span className="required">*</span> are required
                        </span>
                      </div>

                      <button
                        onClick={() => submitQuotation(request.userId, request.transportId, request)}
                        className="transport-submit"
                        disabled={!validateForm(request.transportId)}
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Previous Submissions Side Container */}
        <div className="previous-submissions-container">
          <div className="submissions-header">
            <h3>Previous Submissions</h3>
          </div>

          {previousSubmissions.length === 0 ? (
            <div className="no-submissions">
              <p>No previous submissions found.</p>
            </div>
          ) : (
            <div className="submissions-list">
              {previousSubmissions.map((submission, index) => (
                <div key={index} className="submission-card">
                  <div className="submission-header">
                    <div className="route-info">
                      <MapPin size={14} />
                      <span className="location">{submission.destinationFrom}</span>
                      <span className="arrow">➡️</span>
                      <MapPin size={14} />
                      <span className="location">{submission.destinationTo}</span>
                    </div>
                  </div>
                  <div className="submission-details">
                    <div className="detail-row">
                      <span className="detail-label">
                        <Package size={14} /> Commodity:
                      </span>
                      <span className="detail-value">{submission.commodity}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <Package size={14} /> Quantity:
                      </span>
                      <span className="detail-value">{submission.quantity} kg</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <FaRupeeSign size={14} /> Price:
                      </span>
                      <span className="detail-value">{submission.price}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <Calendar size={14} /> Availability:
                      </span>
                      <span className="detail-value">{submission.availability}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <Calendar size={14} /> Delivery:
                      </span>
                      <span className="detail-value">{submission.estimatedDelivery}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <User size={14} /> Transporter:
                      </span>
                      <span className="detail-value">{submission.transporter?.name}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        <Phone size={14} /> Contact:
                      </span>
                      <span className="detail-value">{submission.transporter?.contact}</span>
                    </div>
                    {submission.notes && (
                      <div className="detail-row">
                        <span className="detail-label">
                          <FileText size={14} /> Notes:
                        </span>
                        <span className="detail-value">{submission.notes}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">
                        <Clock size={14} /> Submitted:
                      </span>
                      <span className="detail-value">{new Date(submission.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransportGoodsReplies;
